import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

import Icon from '../../../components/AppIcon';

const BusinessVerificationTab = ({ profile, onSave }) => {
  const [formData, setFormData] = useState({
    businessName: profile?.businessName || '',
    siretNumber: profile?.siretNumber || '',
    vatNumber: profile?.vatNumber || '',
    businessAddress: profile?.businessAddress || '',
    paymentMethod: profile?.paymentMethod || '',
    bankDetails: profile?.bankDetails || {
      iban: '',
      bic: '',
      accountHolder: ''
    },
    paypalEmail: profile?.paypalEmail || '',
    taxDocuments: profile?.taxDocuments || [],
    affiliatePrograms: profile?.affiliatePrograms || [],
    complianceStatus: profile?.complianceStatus || {}
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const paymentMethodOptions = [
    { value: 'bank_transfer', label: 'Virement bancaire' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'wise', label: 'Wise (ex-TransferWise)' }
  ];

  const affiliateProgramOptions = [
    { value: 'venue_booking', label: 'Réservation de venues' },
    { value: 'artist_management', label: 'Gestion d\'artistes' },
    { value: 'event_promotion', label: 'Promotion d\'événements' },
    { value: 'equipment_rental', label: 'Location d\'équipement' },
    { value: 'music_licensing', label: 'Licences musicales' }
  ];

  const handleInputChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDocumentUpload = async (event, docType) => {
    const files = Array.from(event?.target?.files);
    setUploadingDoc(true);

    try {
      // Mock document upload
      const uploadedDocs = files?.map(file => ({
        id: Date.now() + Math.random(),
        name: file?.name,
        type: docType,
        size: file?.size,
        url: URL.createObjectURL(file),
        uploadDate: new Date()?.toISOString(),
        status: 'pending'
      }));

      setFormData(prev => ({
        ...prev,
        taxDocuments: [...prev?.taxDocuments, ...uploadedDocs]
      }));
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRemoveDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      taxDocuments: prev?.taxDocuments?.filter(doc => doc?.id !== docId)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      businessName: profile?.businessName || '',
      siretNumber: profile?.siretNumber || '',
      vatNumber: profile?.vatNumber || '',
      businessAddress: profile?.businessAddress || '',
      paymentMethod: profile?.paymentMethod || '',
      bankDetails: profile?.bankDetails || {
        iban: '',
        bic: '',
        accountHolder: ''
      },
      paypalEmail: profile?.paypalEmail || '',
      taxDocuments: profile?.taxDocuments || [],
      affiliatePrograms: profile?.affiliatePrograms || [],
      complianceStatus: profile?.complianceStatus || {}
    });
    setIsEditing(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <Icon name="CheckCircle" size={16} className="text-success" />;
      case 'pending':
        return <Icon name="Clock" size={16} className="text-warning" />;
      case 'rejected':
        return <Icon name="XCircle" size={16} className="text-error" />;
      default:
        return <Icon name="AlertCircle" size={16} className="text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Vérifié';
      case 'pending':
        return 'En attente';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'Non fourni';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Vérification Business</h2>
        {!isEditing ? (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
          >
            Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Sauvegarder
            </Button>
          </div>
        )}
      </div>
      {/* Business Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Informations légales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nom de l'entreprise"
            type="text"
            value={formData?.businessName}
            onChange={(e) => handleInputChange('businessName', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Nom commercial ou raison sociale"
          />

          <Input
            label="Numéro SIRET"
            type="text"
            value={formData?.siretNumber}
            onChange={(e) => handleInputChange('siretNumber', e?.target?.value)}
            disabled={!isEditing}
            placeholder="14 chiffres"
          />

          <Input
            label="Numéro TVA"
            type="text"
            value={formData?.vatNumber}
            onChange={(e) => handleInputChange('vatNumber', e?.target?.value)}
            disabled={!isEditing}
            placeholder="FR + 11 chiffres"
          />

          <div className="md:col-span-2">
            <Input
              label="Adresse de l'entreprise"
              type="text"
              value={formData?.businessAddress}
              onChange={(e) => handleInputChange('businessAddress', e?.target?.value)}
              disabled={!isEditing}
              placeholder="Adresse complète"
            />
          </div>
        </div>
      </div>
      {/* Payment Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Informations de paiement</h3>
        
        <Select
          label="Méthode de paiement préférée"
          options={paymentMethodOptions}
          value={formData?.paymentMethod}
          onChange={(value) => handleInputChange('paymentMethod', value)}
          disabled={!isEditing}
        />

        {formData?.paymentMethod === 'bank_transfer' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <Input
              label="IBAN"
              type="text"
              value={formData?.bankDetails?.iban}
              onChange={(e) => handleInputChange('bankDetails.iban', e?.target?.value)}
              disabled={!isEditing}
              placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
            />

            <Input
              label="Code BIC"
              type="text"
              value={formData?.bankDetails?.bic}
              onChange={(e) => handleInputChange('bankDetails.bic', e?.target?.value)}
              disabled={!isEditing}
              placeholder="BNPAFRPPXXX"
            />

            <div className="md:col-span-2">
              <Input
                label="Titulaire du compte"
                type="text"
                value={formData?.bankDetails?.accountHolder}
                onChange={(e) => handleInputChange('bankDetails.accountHolder', e?.target?.value)}
                disabled={!isEditing}
                placeholder="Nom du titulaire"
              />
            </div>
          </div>
        )}

        {formData?.paymentMethod === 'paypal' && (
          <div className="p-4 bg-muted rounded-lg">
            <Input
              label="Email PayPal"
              type="email"
              value={formData?.paypalEmail}
              onChange={(e) => handleInputChange('paypalEmail', e?.target?.value)}
              disabled={!isEditing}
              placeholder="votre@email.com"
            />
          </div>
        )}
      </div>
      {/* Tax Documents */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Documents fiscaux</h3>
          {isEditing && (
            <div className="flex gap-2">
              <input
                type="file"
                id="tax-doc-upload"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentUpload(e, 'tax')}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('tax-doc-upload')?.click()}
                loading={uploadingDoc}
                iconName="Upload"
                iconPosition="left"
              >
                Ajouter document
              </Button>
            </div>
          )}
        </div>

        {formData?.taxDocuments?.length > 0 ? (
          <div className="space-y-3">
            {formData?.taxDocuments?.map((doc) => (
              <div key={doc?.id} className="flex items-center justify-between bg-muted rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{doc?.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusIcon(doc?.status)}
                      <span className="text-xs text-muted-foreground">
                        {getStatusText(doc?.status)}
                      </span>
                    </div>
                  </div>
                </div>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveDocument(doc?.id)}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Aucun document fiscal ajouté</p>
            {isEditing && (
              <p className="text-sm mt-2">Ajoutez vos documents fiscaux pour la vérification</p>
            )}
          </div>
        )}
      </div>
      {/* Affiliate Programs */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Programmes d'affiliation</h3>
        <Select
          label="Programmes souscrits"
          description="Sélectionnez les programmes auxquels vous souhaitez participer"
          options={affiliateProgramOptions}
          value={formData?.affiliatePrograms}
          onChange={(value) => handleInputChange('affiliatePrograms', value)}
          disabled={!isEditing}
          multiple
        />
      </div>
      {/* Compliance Status */}
      <div className="bg-muted rounded-lg p-4">
        <h3 className="font-medium text-foreground mb-4">Statut de conformité</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">Informations légales</span>
            <div className="flex items-center gap-2">
              {getStatusIcon(formData?.businessName && formData?.siretNumber ? 'verified' : 'pending')}
              <span className="text-sm text-muted-foreground">
                {getStatusText(formData?.businessName && formData?.siretNumber ? 'verified' : 'pending')}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">Informations de paiement</span>
            <div className="flex items-center gap-2">
              {getStatusIcon(formData?.paymentMethod ? 'verified' : 'pending')}
              <span className="text-sm text-muted-foreground">
                {getStatusText(formData?.paymentMethod ? 'verified' : 'pending')}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">Documents fiscaux</span>
            <div className="flex items-center gap-2">
              {getStatusIcon(formData?.taxDocuments?.length > 0 ? 'pending' : 'not_provided')}
              <span className="text-sm text-muted-foreground">
                {getStatusText(formData?.taxDocuments?.length > 0 ? 'pending' : 'not_provided')}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">Programmes d'affiliation</span>
            <div className="flex items-center gap-2">
              {getStatusIcon(formData?.affiliatePrograms?.length > 0 ? 'verified' : 'not_provided')}
              <span className="text-sm text-muted-foreground">
                {getStatusText(formData?.affiliatePrograms?.length > 0 ? 'verified' : 'not_provided')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessVerificationTab;